import { LearnerProfile } from '@/domain/precon-supporter/learner-profile/LearnerProfile';
import { LearnerProfileRepository } from '@/domain/precon-supporter/learner-profile/LearnerProfileRepository';
import { IndexedDBClient } from './IndexedDBClient';
import { STORE_NAMES } from './schema';

/**
 * IndexedDBを使用したLearnerProfileRepositoryの実装
 *
 * 固定キー 'default' でデータを保存・取得する。
 */
export class IndexedDBLearnerProfileRepository
  implements LearnerProfileRepository
{
  private static readonly PROFILE_KEY = 'default';

  constructor(private readonly client: IndexedDBClient) {}

  /**
   * 受講者プロフィールを保存
   */
  async save(profile: LearnerProfile): Promise<void> {
    try {
      const store = await this.client.transaction(
        STORE_NAMES.LEARNER_PROFILE,
        'readwrite'
      );

      const data = profile.toData();

      await new Promise<void>((resolve, reject) => {
        const request = store.put(
          data,
          IndexedDBLearnerProfileRepository.PROFILE_KEY
        );

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          reject(
            new Error(
              `LearnerProfileの保存に失敗しました: ${request.error?.message}`
            )
          );
        };
      });
    } catch (error) {
      throw new Error(
        `LearnerProfileの保存中にエラーが発生しました: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  /**
   * 受講者プロフィールを取得
   */
  async find(): Promise<LearnerProfile | null> {
    try {
      const store = await this.client.transaction(
        STORE_NAMES.LEARNER_PROFILE,
        'readonly'
      );

      const data = await new Promise<any>((resolve, reject) => {
        const request = store.get(
          IndexedDBLearnerProfileRepository.PROFILE_KEY
        );

        request.onsuccess = () => {
          resolve(request.result);
        };

        request.onerror = () => {
          reject(
            new Error(
              `LearnerProfileの取得に失敗しました: ${request.error?.message}`
            )
          );
        };
      });

      if (!data) {
        return null;
      }

      return this.mapToDomain(data);
    } catch (error) {
      throw new Error(
        `LearnerProfileの取得中にエラーが発生しました: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  /**
   * 受講者プロフィールを削除
   */
  async delete(): Promise<void> {
    try {
      const store = await this.client.transaction(
        STORE_NAMES.LEARNER_PROFILE,
        'readwrite'
      );

      await new Promise<void>((resolve, reject) => {
        const request = store.delete(
          IndexedDBLearnerProfileRepository.PROFILE_KEY
        );

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          reject(
            new Error(
              `LearnerProfileの削除に失敗しました: ${request.error?.message}`
            )
          );
        };
      });
    } catch (error) {
      throw new Error(
        `LearnerProfileの削除中にエラーが発生しました: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  /**
   * IndexedDBデータをドメインモデルに変換
   * 既存データ（v1）にはnicknameがないため、undefinedの場合はnullとして扱う
   */
  private mapToDomain(data: {
    wantsAdvancedCourse: boolean;
    nickname?: string | null;
    createdAt: string;
    updatedAt: string;
  }): LearnerProfile {
    return LearnerProfile.reconstruct({
      wantsAdvancedCourse: data.wantsAdvancedCourse,
      nickname: data.nickname ?? null,
      createdAt: new Date(data.createdAt),
      updatedAt: new Date(data.updatedAt),
    });
  }
}
